import { ProForADayConfirmation } from "MVVM/Components/Modals/ProForADayConfirmation";
import { usePortal } from "MVVM/Hooks/usePortal";
import { useUser } from "MVVM/Hooks/useUser";
import styled from "styled-components";
import { useState } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";

import theme from "theme/themeThree";
import { AdRemovalCTAViewModel } from "../ViewModel";
import { CreatorCreditIcon } from "MVVM/Views/Pricing/Components/Table/CreatorCreditIcon";
import { analytics } from "MVVM/Analytics";
import { AllstarModalState } from "MVVM/State/modals";
import { ModalType } from "MVVM/@types";
import { URLS } from "utils";

export const ProForADayButton = ({
  location,
  onSuccess,
  fullWidth,
}: {
  location: string;
  onSuccess?: () => void;
  fullWidth?: boolean;
}) => {
  const [proForADayModalOpen, setProForADayModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setAllstarModalState] = useRecoilState(AllstarModalState);
  const { subscriptionCreate } = AdRemovalCTAViewModel();
  const { allstarUser } = useUser();
  const history = useHistory();

  const userGroup = allstarUser?.user?.userGroup?.type;
  const hasPremiumAccess = ["PRO", "CREATOR_PLAT"].includes(userGroup);
  const { Portal: ProForADayPortal } = usePortal(
    <ProForADayConfirmation
      isOpen={proForADayModalOpen}
      setIsOpen={setProForADayModalOpen}
      onConfirmationClose={onSuccess}
    />,
    "root",
  );
  const handleProForADay = async () => {
    if (hasPremiumAccess) return;
    if (!allstarUser.loggedIn) {
      return history.push({
        pathname: URLS.LOGIN,
        search: `?next=${encodeURIComponent(window.location.pathname)}`,
      });
    }

    analytics.track("Button - Click", {
      locationIdentifier: "pro_for_a_day_cta_attempt",
      location,
    });

    setLoading(true);
    const result = await subscriptionCreate({ variables: { input: {} } });
    const errors = result?.data?.subscriptionCreate?.errors;
    if (errors?.length > 0) {
      analytics.track("Component Viewed", {
        locationIdentifier: "pro_for_a_day_insufficient_credits",
        location,
      });
      setAllstarModalState({
        isOpen: ModalType.MicroTransactions,
        data: { type: "pro_for_a_day" },
        functions: {},
      });
      setLoading(false);
      return;
    }

    analytics.track("Button - Click", {
      locationIdentifier: "pro_for_a_day_success",
      location,
    });
    setProForADayModalOpen(true);
    setLoading(false);
  };

  if (allstarUser?.user?.userGroup?.type !== "FREE") return null;

  return (
    <>
      {proForADayModalOpen && ProForADayPortal}
      <Button
        onClick={handleProForADay}
        disabled={loading || hasPremiumAccess}
        fullWidth={fullWidth}
      >
        <Flex alignItems="center" justifyContent="center" style={{ gap: 6 }}>
          <Text variant="text.labelSmall" fontWeight={600} color="ice">
            {loading
              ? "Loading..."
              : hasPremiumAccess
              ? "Already Pro"
              : "Pro for a Day"}
          </Text>
          <Flex alignItems="center" style={{ gap: 4 }}>
            <CreatorCreditIcon size={14} addMargin={false} />
            <Text variant="text.labelSmall" fontWeight={600} color="ice">
              1
            </Text>
          </Flex>
        </Flex>
      </Button>
    </>
  );
};

const Button = styled(Box)<{ disabled?: boolean; fullWidth?: boolean }>`
  background: ${theme.colors.envy};
  border-radius: 8px;
  color: ${theme.colors.ice};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  flex-shrink: 0;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  padding: 8px 16px;
  text-align: center;
  transition: all 0.2s ease;
  white-space: nowrap;
  width: ${(props) => (props.fullWidth ? "100%" : "auto")};

  &:hover {
    transform: ${(props) => (props.disabled ? "none" : "translateY(-1px)")};
    box-shadow: ${(props) =>
      props.disabled ? "none" : "0 4px 12px rgba(0, 0, 0, 0.3)"};
  }

  &:active {
    transform: ${(props) => (props.disabled ? "none" : "translateY(0)")};
  }

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    padding: 6px 12px;
  }
`;
